import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import CustomerSearchBar from './components/CustomerSearchBar';
import CustomerCard from './components/CustomerCard';
import CustomerDetailDrawer from './components/CustomerDetailDrawer';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import { customerService } from '../../services/customerService';
import mobileConfigService from '../../services/mobileConfigService';

const detectPlatform = () => {
  const ua = navigator?.userAgent?.toLowerCase() || '';
  if (/iphone|ipad|ipod/?.test(ua)) return 'ios';
  if (/android/?.test(ua)) return 'android';
  return 'ios';
};

const MobileCustomerManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [visibility, setVisibility] = useState({ customer_list: true, customer_details: true, quick_actions: true });
  const platform = detectPlatform();

  const fetchVisibility = useCallback(async () => {
    try {
      const config = await mobileConfigService?.getScreenVisibility(platform);
      if (config && typeof config === 'object') {
        setVisibility(prev => ({
          ...prev,
          customer_list: config?.customer_list !== false,
          customer_details: config?.customer_details !== false,
          quick_actions: config?.quick_actions !== false,
        }));
      }
    } catch (err) {
      console.error('Failed to load mobile config:', err);
    }
  }, [platform]);

  const fetchCustomers = useCallback(async () => {
    try {
      const { data, error } = await customerService?.getAll();
      if (!error && data) setCustomers(data);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
  }, []);

  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    await Promise.all([fetchVisibility(), fetchCustomers()]);
    setLoading(false);
    setRefreshing(false);
  }, [fetchVisibility, fetchCustomers]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredCustomers = customers?.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery?.toLowerCase();
    return ((c?.companyName || c?.company_name || '')?.toLowerCase()?.includes(q) ||
    (c?.contactPerson || c?.contact_person || '')?.toLowerCase()?.includes(q) || (c?.email || '')?.toLowerCase()?.includes(q));
  });

  const handleCreateBid = (customer) => {
    navigate('/bid-management', { state: { prefillCustomer: customer } });
  };

  const handleCreateProject = (customer) => {
    navigate('/project-management', { state: { prefillCustomer: customer } });
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-lg font-bold text-foreground">Customers</h1>
              <p className="text-xs text-muted-foreground capitalize">{platform} · {customers?.length} total</p>
            </div>
            <button
              onClick={() => loadAll(true)}
              disabled={refreshing}
              className="w-9 h-9 rounded-full bg-muted flex items-center justify-center"
            >
              <Icon name="RefreshCw" size={16} className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {visibility?.customer_list && (
            <CustomerSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery('')}
            />
          )}
        </div>

        {/* Content */}
        <div className="px-4 py-4 pb-28 space-y-3">
          {loading ? (
            Array.from({ length: 4 })?.map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-4 h-36 animate-pulse">
                <div className="flex gap-3 mb-3">
                  <div className="w-11 h-11 bg-muted rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
                <div className="h-8 bg-muted rounded-xl" />
              </div>
            ))
          ) : !visibility?.customer_list ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Icon name="EyeOff" size={28} className="text-muted-foreground" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">Section hidden</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Customer list is hidden for {platform === 'ios' ? 'iOS' : 'Android'}.
              </p>
              <button onClick={() => navigate('/mobile-app-configuration')} className="mt-4 text-sm font-medium text-accent underline">
                Go to Configuration
              </button>
            </div>
          ) : filteredCustomers?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Icon name="Users" size={28} className="text-muted-foreground" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">
                {searchQuery ? 'No results found' : 'No customers yet'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery ? `No customers match "${searchQuery}"` : 'Customers will appear here once added.'}
              </p>
            </div>
          ) : (
            filteredCustomers?.map(customer => (
              <CustomerCard
                key={customer?.id}
                customer={customer}
                onOpenDetail={setSelectedCustomer}
                onCreateBid={handleCreateBid}
                onCreateProject={handleCreateProject}
              />
            ))
          )}
        </div>

        {/* FAB - Add Customer */}
        {visibility?.quick_actions && (
          <button
            onClick={() => navigate('/customer-management')}
            className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-accent shadow-xl flex items-center justify-center active:scale-95 transition-transform"
          >
            <Icon name="Plus" size={24} color="white" />
          </button>
        )}

        {/* Detail Drawer */}
        {selectedCustomer && visibility?.customer_details && (
          <CustomerDetailDrawer
            customer={selectedCustomer}
            onClose={() => setSelectedCustomer(null)}
            onCreateBid={handleCreateBid}
            onCreateProject={handleCreateProject}
          />
        )}

        {/* Bottom Nav */}
        <MobileBottomNav />
      </div>
    </MainLayout>
  );
};

export default MobileCustomerManagement;
